import React, { useState, useEffect, useCallback } from 'react'
import axiosInstance from '../../services/api/axios'
import { getCookie } from '../../utils/common'
import { useUser } from '../../context/UserContext'
import {
  Dialog,
  DialogContent,
  Card,
  CardBody,
} from "@material-tailwind/react";
import { Eye, Loader2, Edit3, Save, XCircle, X } from "lucide-react";
import { ViewButton, CloseButton, EditButton, PrimaryButton, OutlineButton } from '../../components/Buttons';
import { toast } from 'react-toastify';
import CustomInput from '../../components/CustomInput';

function DemandLetterApproval({ hideHeader = false }) {
  const { userInfo } = useUser()
  const [attachments, setAttachments] = useState([])
  const [filteredAttachments, setFilteredAttachments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({
    search: '',
    status: ['PENDING'] // Default to pending status
  })
  const [generatingDemandLetters, setGeneratingDemandLetters] = useState(false)
  const [demandLetterMessage, setDemandLetterMessage] = useState('')
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [editingAttachment, setEditingAttachment] = useState(null)
  const [editingContent, setEditingContent] = useState('')
  const [previewContent, setPreviewContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [approvalModalOpen, setApprovalModalOpen] = useState(false)
  const [scheduledDate, setScheduledDate] = useState('')
  const [savedAttachments, setSavedAttachments] = useState(new Set())
  const [approvalStatus, setApprovalStatus] = useState('APPROVED')
  const [passphrase, setPassphrase] = useState('')
  const [viewMode, setViewMode] = useState('edit') // 'edit' or 'view'
  const [isEditing, setIsEditing] = useState(false)

  // ContentEditable Editor State
  const [editorRef, setEditorRef] = useState(null)

  // Sync editor content to preview with debouncing
  const syncEditorToPreview = useCallback(() => {
    if (editorRef) {
      setPreviewContent(editorRef.innerHTML);
    }
  }, [editorRef]);

  // Set up inline editing for A4 document
  useEffect(() => {
    if (editorRef && viewMode === 'edit' && isEditing) {
      // Wait for DOM to be ready
      setTimeout(() => {
        // Make the entire editor editable when in edit mode
        editorRef.setAttribute('contenteditable', 'true');
        editorRef.style.outline = '2px dashed #3b82f6';
        editorRef.style.outlineOffset = '2px';
        editorRef.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
        editorRef.style.minHeight = '200px';
        editorRef.style.padding = '10px';
        
        // Add input listener for changes
        const handleInput = () => {
          setHasUnsavedChanges(true);
          // Debounce the preview update
          clearTimeout(window.previewUpdateTimer);
          window.previewUpdateTimer = setTimeout(() => {
            setPreviewContent(editorRef.innerHTML);
          }, 300);
        };
        
        editorRef.addEventListener('input', handleInput);
        
        // Store the cleanup function
        editorRef._cleanup = () => {
          editorRef.removeEventListener('input', handleInput);
          clearTimeout(window.previewUpdateTimer);
        };
        
        // Focus the editor
        setTimeout(() => editorRef.focus(), 100);
      }, 100);
    } else if (editorRef && viewMode === 'view') {
      // Make read-only in view mode
      editorRef.setAttribute('contenteditable', 'false');
      editorRef.style.outline = 'none';
      editorRef.style.backgroundColor = 'transparent';
      editorRef.style.padding = '0';
    }

    // Cleanup function
    return () => {
      if (editorRef && editorRef._cleanup) {
        editorRef._cleanup();
      }
    };
  }, [editorRef, viewMode, isEditing]);

  // --- Helper Functions for Content Conversion (SIMPLIFIED FOR BASE64 ONLY) ---

  // Helper function to convert hex string to regular string
  const hexToString = (hex) => {
    try {
      let result = '';
      for (let i = 0; i < hex.length; i += 2) {
        const hexByte = hex.substr(i, 2);
        const charCode = parseInt(hexByte, 16);
        if (charCode > 0) { // Skip null bytes
          result += String.fromCharCode(charCode);
        }
      }
      return result;
    } catch (error) {
      console.error('Error converting hex to string:', error);
      return `<div class="p-4 bg-red-100 rounded-lg">
                <p class="text-sm text-red-600">Error converting hexadecimal content to HTML</p>
                <pre class="text-xs mt-2">${hex}</pre>
              </div>`;
    }
  };

  // Converts Base64 string to HTML string or handles hex content
  const convertContentToHtml = useCallback((content) => {
    if (!content) return '';
    
    try {
      // First try to decode as base64
      const htmlText = atob(content);

      
      // Check if the decoded result looks like hex data
      if (/^[0-9a-fA-F]+$/.test(htmlText)) {
        console.log('Decoded content appears to be hexadecimal, converting to HTML...');
        // Convert hex to string
        const actualHtml = hexToString(htmlText);
        console.log('Converted HTML:', actualHtml);
        return actualHtml;
      }
      
      return htmlText;
    } catch (error) {
      console.error('Error decoding Base64 content:', error);
      console.log('Raw content:', content);
      
      // If it's not base64, display the raw content
      return `<div class="p-4 bg-gray-100 rounded-lg">
                <p class="text-sm text-gray-600 mb-2">Raw Content:</p>
                <pre class="text-xs break-all whitespace-pre-wrap">${content}</pre>
                <p class="text-xs text-gray-500 mt-2">This content could not be decoded as Base64 HTML.</p>
              </div>`;
    }
  }, []);

  // Helper function to determine if content is PDF
  const isPdfContent = useCallback((content) => {
    if (!content) return false;
    
    try {
      const decoded = atob(content);
      // Check if it starts with PDF header
      return decoded.startsWith('%PDF-');
    } catch (error) {
      return false;
    }
  }, []);

  // Helper function to create PDF blob URL
  const createPdfBlobUrl = useCallback((content) => {
    try {
      const binaryString = atob(content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating PDF blob:', error);
      return null;
    }
  }, []);

  // Extract editable content from container div (plain text only)
  const extractEditableContent = useCallback((htmlContent) => {
    try {
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      // Find the container div
      const containerDiv = tempDiv.querySelector('.container');
      if (!containerDiv) {
        console.log('No container div found, returning empty string');
        return '';
      }
      
      // Extract only the text content from the container, preserving line breaks
      let editableText = '';
      
      // Process each child node to preserve structure
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          editableText += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Skip images and non-text elements
          if (['IMG', 'STYLE', 'SCRIPT'].includes(node.tagName)) {
            return;
          }
          
          // Add line breaks for block elements
          if (['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR'].includes(node.tagName)) {
            if (editableText && !editableText.endsWith('\n')) {
              editableText += '\n';
            }
          }
          
          // Process child nodes
          for (let child of node.childNodes) {
            processNode(child);
          }
          
          // Add line break after block elements
          if (['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.tagName)) {
            editableText += '\n';
          }
        }
      };
      
      processNode(containerDiv);
      
      // Clean up extra line breaks
      editableText = editableText
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      
      console.log('Extracted editable content:', editableText);
      return editableText;
    } catch (error) {
      console.error('Error extracting editable content:', error);
      return '';
    }
  }, []);

  // Rebuild HTML with updated container content
  const rebuildHtmlWithEditableContent = useCallback((originalHtml, newHtmlContent) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = originalHtml;
      
      const containerDiv = tempDiv.querySelector('.container');
      if (!containerDiv) {
        console.log('No container div found, returning original content');
        return originalHtml;
      }
      
      // Replace the content of the container div with the new HTML content directly
      containerDiv.innerHTML = newHtmlContent;
      
      // Ensure we return the complete HTML structure including styles
      const completeHtml = tempDiv.innerHTML;
      console.log('Rebuilt complete HTML:', completeHtml);
      return completeHtml;
    } catch (error) {
      console.error('Error rebuilding HTML:', error);
      return originalHtml;
    }
  }, []);

  // --- Data Fetching ---

  const fetchAttachments = useCallback(async (customerId = null) => {
    setLoading(true)
    setError(null)

    try {
      const params = {}

      if (filters.search) params.search = filters.search

      const response = await axiosInstance.get('/notifications/queue/attachment', {
        params
      })

      setAttachments(response)
    } catch (err) {
      console.error('Error fetching notification attachments:', err)
      setError('Failed to fetch notification attachments')
    } finally {
      setLoading(false)
    }
  }, [filters]);

  // Filter attachments based on current filters
  const applyFilters = useCallback(() => {
    let filtered = [...attachments]

    // Apply status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(notification => {
        const displayStatus = getDisplayStatus(notification)
        return filters.status.includes(displayStatus)
      })
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(notification => {
        const recipientEmail = notification.notificationQueue?.recipientEmail || ''
        const subject = notification.notificationQueue?.subject || ''
        const customerId = notification.notificationQueue?.customerId || ''
        const loginId = notification.notificationQueue?.loginId || ''
        
        return (
          recipientEmail.toLowerCase().includes(searchLower) ||
          subject.toLowerCase().includes(searchLower) ||
          customerId.toString().includes(searchLower) ||
          loginId.toString().includes(searchLower)
        )
      })
    }

    setFilteredAttachments(filtered)
  }, [attachments, filters])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  useEffect(() => {
    fetchAttachments()
    window.testDemandLetters = sendScheduledDemandLetters
  }, [fetchAttachments]);

  // --- Filter Handling ---

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSearchChange = (value) => {
    handleFilterChange('search', value)
    // No need to call fetchAttachments since we filter locally
  }



  const handleStatusChange = (status) => {
    setFilters(prev => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status]
    }))
  }

  // Helper function to determine display status
  const getDisplayStatus = (notification) => {
    const queueStatus = notification.notificationQueue?.status
    const scheduledAt = notification.notificationQueue?.scheduledAt
    
    // If status is PENDING and has scheduled date, show as IN_PROGRESS
    if (queueStatus === 'PENDING' && scheduledAt) {
      return 'IN_PROGRESS'
    }
    
    return queueStatus || 'UNKNOWN'
  }

  // --- Demand Letter Generation ---

  async function sendScheduledDemandLetters() {
    setGeneratingDemandLetters(true)
    setDemandLetterMessage('')

    try {
      const authToken = getCookie('accessToken');

      const response = await axiosInstance.post(
        '/cron/send-scheduled-demand-letters',
        {},
        {
          headers: {
            Authorization: authToken ? `Bearer ${authToken}` : undefined,
          },
        }
      );

      if (response && response.status >= 200 && response.status < 300) {
        setDemandLetterMessage('✅ Demand letters sent successfully!');
        console.log('✅ Demand letters sent successfully!', response.data);
        setTimeout(() => {
          fetchAttachments();
        }, 1000);
      } else {
        setDemandLetterMessage('❌ Failed to send demand letters');
        console.error('❌ Failed to send demand letters:', response?.data);
      }
    } catch (error) {
      setDemandLetterMessage('❌ Error: ' + (error?.response?.data?.message || error.message));
      console.error('❌ Error:', error);
    } finally {
      setGeneratingDemandLetters(false);
      setTimeout(() => {
        setDemandLetterMessage('');
      }, 5000);
    }
  }

  // --- UI Handlers ---

  function formatDate(dateString) {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  const handleViewNotification = (notification) => {
    setSelectedNotification(notification)
    setViewModalOpen(true)
    
    // Initialize editing content if there's an attachment
    if (notification.notificationAttachment && notification.notificationAttachment.length > 0) {
      const htmlContent = convertContentToHtml(notification.notificationAttachment[0].content)
      setEditingContent(htmlContent)
      setPreviewContent(htmlContent)
    }
  }

  const closeViewModal = () => {
    setViewModalOpen(false)
    setSelectedNotification(null)
    setEditingAttachment(null)
    setEditingContent('')
    setPreviewContent('')
    setHasUnsavedChanges(false)
    setViewMode('edit')
    setIsEditing(false)
    
    // Cleanup any PDF blob URLs
    if (selectedNotification?.notificationAttachment?.[0]) {
      const pdfUrl = createPdfBlobUrl(selectedNotification.notificationAttachment[0].content);
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    }
  }

  const handleEditAttachment = (attachment) => {
    const htmlContent = convertContentToHtml(attachment.content)
    setEditingAttachment(attachment)
    
    // Use the full HTML content for the editor (with styles)
    setEditingContent(htmlContent)
    setPreviewContent(htmlContent)
    setEditModalOpen(true)
  }

  const handleSaveAttachment = () => {
    if (!selectedNotification || !editorRef) return
    
    setIsSaving(true)
    try {
      // Get the current HTML content from the editor
      const editedHtml = editorRef.innerHTML;
      
      console.log('=== SAVE CONTENT DEBUG ===');
      console.log('Edited HTML from editor:', editedHtml);
      console.log('Edited HTML length:', editedHtml.length);
      
      // Convert back to base64
      const base64Content = btoa(editedHtml)
      console.log('Final base64 content being saved length:', base64Content.length);
      console.log('=== END SAVE DEBUG ===');

      // Update local state
      setAttachments(prev => prev.map(notification => {
        const currentNotificationQueueId = notification.notificationQueue?.notificationQueueId || notification.notificationQueueId;
        const selectedNotificationQueueId = selectedNotification.notificationQueue?.notificationQueueId || selectedNotification.notificationQueueId;
        if (currentNotificationQueueId === selectedNotificationQueueId && notification.notificationAttachment) {
          return {
            ...notification,
            notificationAttachment: notification.notificationAttachment.map(att =>
              att.notificationAttachmentId === selectedNotification.notificationAttachment[0].notificationAttachmentId
                ? { ...att, content: base64Content }
                : att
            )
          }
        }
        return notification
      }))

      // Update the selected notification with new content
      setSelectedNotification(prev => ({
        ...prev,
        notificationAttachment: prev.notificationAttachment.map(att =>
          att.notificationAttachmentId === prev.notificationAttachment[0].notificationAttachmentId
            ? { ...att, content: base64Content }
            : att
        )
      }))

      // Update editing content
      setEditingContent(editedHtml)
      setPreviewContent(editedHtml)

      // Mark as saved
      setHasUnsavedChanges(false)
      
      // Track this attachment as saved
      setSavedAttachments(prev => new Set([...prev, selectedNotification.notificationAttachment[0].notificationAttachmentId]))
      
      toast.success('Changes saved successfully! You can now approve and send.')
    } catch (error) {
      console.error('Failed to save attachment:', error)
      toast.error('Failed to save attachment: ' + error.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleApproveNotification = (notification) => {
    setSelectedNotification(notification)
    setApprovalStatus('APPROVED')
    setApprovalModalOpen(true)
  }

  const handleDeclineNotification = (notification) => {
    setSelectedNotification(notification)
    setApprovalStatus('DECLINED')
    setApprovalModalOpen(true)
  }

  const handleSendForApproval = () => {
    if (!selectedNotification) return
    setApprovalModalOpen(true)
  }

  const handleApproveAndSend = async () => {
    if (!selectedNotification || !passphrase) return

    setIsApproving(true)
    try {
      console.log("----------------", selectedNotification);
      console.log("UserInfo:", userInfo);
      console.log("Approval Status:", approvalStatus);
      console.log("Scheduled Date:", scheduledDate);
      console.log("Passphrase:", passphrase);
      
      // Debug notification structure
      console.log("Selected notification structure:");
      console.log("- notificationQueue:", selectedNotification.notificationQueue);
      console.log("- notificationQueueId:", selectedNotification.notificationQueue?.notificationQueueId);
      console.log("- notificationAttachment:", selectedNotification.notificationAttachment);
      console.log("- notificationAttachment length:", selectedNotification.notificationAttachment?.length);
      
      // Prepare the approval request body
      const approvalData = {
        notificationQueueId: selectedNotification.notificationQueue?.notificationQueueId || selectedNotification.notificationQueueId,
        loginId: userInfo?.loginId || 0, // Get from user context
        scheduledAt: approvalStatus === 'APPROVED' ? new Date(scheduledDate).toISOString().slice(0, -1) : null,
        passphrase: passphrase,
        notificationAttachments: selectedNotification.notificationAttachment?.map(att => {
          return {
            notificationAttachmentId: att.notificationAttachmentId,
            notificationQueueId: selectedNotification.notificationQueue?.notificationQueueId || selectedNotification.notificationQueueId,
            content: att.content,
            fileName: att.fileName,
            contentType: att.contentType,
            storageType: "BYTES"
          };
        }) || []
      }

      // Set status based on action
      if (approvalStatus === 'APPROVED') {
        approvalData.status = null // Send null for approval
      } else if (approvalStatus === 'DECLINED') {
        approvalData.status = "DECLINED"
      }
      
      console.log('=== APPROVAL REQUEST DEBUG ===');
      console.log('Approval data being sent:', approvalData);
      console.log('Notification attachments content:', approvalData.notificationAttachments.map(att => ({
        ...att,
        content: att.content.substring(0, 100) + '...' // Show first 100 chars of base64
      })));
      
      // Debug each attachment object individually
      approvalData.notificationAttachments.forEach((att, index) => {
        console.log(`Attachment ${index} full object:`, att);
        console.log(`Attachment ${index} has notificationQueueId:`, 'notificationQueueId' in att);
        console.log(`Attachment ${index} notificationQueueId value:`, att.notificationQueueId);
      });
      
      // Decode and show what the base64 content actually contains
      approvalData.notificationAttachments.forEach((att, index) => {
        try {
          const decodedContent = atob(att.content);
          console.log(`Attachment ${index} (ID: ${att.notificationAttachmentId}) decoded content length:`, decodedContent.length);
          console.log(`Attachment ${index} (ID: ${att.notificationAttachmentId}) decoded content:`, decodedContent);
          console.log(`Attachment ${index} (ID: ${att.notificationAttachmentId}) starts with:`, decodedContent.substring(0, 200));
          console.log(`Attachment ${index} (ID: ${att.notificationAttachmentId}) ends with:`, decodedContent.substring(decodedContent.length - 200));
        } catch (error) {
          console.log(`Attachment ${index} could not be decoded:`, error);
        }
      });
      
      console.log('=== END APPROVAL DEBUG ===');

      console.log('Sending approval request:', approvalData)

      const response = await axiosInstance.put('/notifications/queue/scheduled-at-status', approvalData)

      console.log('Approval successful:', response)

      // Update local state to reflect the approval
      setAttachments(prev => prev.map(notification => {
        const currentNotificationQueueId = notification.notificationQueue?.notificationQueueId || notification.notificationQueueId;
        const selectedNotificationQueueId = selectedNotification.notificationQueue?.notificationQueueId || selectedNotification.notificationQueueId;
        if (currentNotificationQueueId === selectedNotificationQueueId) {
          return {
            ...notification,
            notificationQueue: {
              ...notification.notificationQueue,
              status: approvalStatus === 'APPROVED' ? 'PENDING' : 'DECLINED',
              scheduledAt: approvalData.scheduledAt
            }
          }
        }
        return notification
      }))

      // Close approval modal and reset form
      setApprovalModalOpen(false)
      setScheduledDate('')
      setApprovalStatus('APPROVED')
      setPassphrase('')
      setSavedAttachments(new Set())

      // Show toast messages instead of alert
      const successMessage = approvalStatus === 'APPROVED' 
        ? 'Notification approved and scheduled successfully!'
        : 'Notification declined successfully!'
      
      // Refresh the data to show updated status
      setTimeout(() => {
        fetchAttachments()
      }, 1000)
      
      if (window && window.toast) {
        window.toast.success(successMessage)
      } else if (typeof toast !== 'undefined') {
        toast.success(successMessage)
      }

    } catch (error) {
      console.error('Failed to approve notification:', error)
      const errorMessage = 'Failed to approve notification: ' + (error?.response?.data?.message || error.message)
      if (window && window.toast) {
        window.toast.error(errorMessage)
      } else if (typeof toast !== 'undefined') {
        toast.error(errorMessage)
      }
    } finally {
      setIsApproving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingAttachment(null)
    setEditingContent('')
    setPreviewContent('')
    setEditModalOpen(false)
    setHasUnsavedChanges(false)
  }

  // --- Render Method ---

  return (
    <div className="p-2">
      {/* Custom styles for TipTap editor to reduce spacing */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .ProseMirror {
            line-height: 1.4 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .ProseMirror p {
            margin: 0.3em 0 !important;
            padding: 0 !important;
          }
          .ProseMirror p:first-child {
            margin-top: 0 !important;
          }
          .ProseMirror p:last-child {
            margin-bottom: 0 !important;
          }
          .ProseMirror h1, .ProseMirror h2, .ProseMirror h3 {
            margin: 0.6em 0 0.3em 0 !important;
          }
          .ProseMirror ul, .ProseMirror ol {
            margin: 0.3em 0 !important;
            padding-left: 1.5em !important;
          }
          .ProseMirror li {
            margin: 0.1em 0 !important;
          }
        `
      }} />
      {!hideHeader && (
        <div className="mb-2">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Demand Letter Approval</h1>
              <p className="text-gray-600">Manage notification queue attachments for demand letters</p>
            </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={sendScheduledDemandLetters}
              disabled={generatingDemandLetters}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center gap-2"
            >
              {generatingDemandLetters ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Generating...
                </>
              ) : (
                <>
                  📧 Generate Demand Letters
                </>
              )}
            </button>
            {demandLetterMessage && (
              <div className={`text-sm px-3 py-1 rounded-md ${demandLetterMessage.includes('✅')
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {demandLetterMessage}
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="p-4">
          <div className="flex items-end gap-4">
           
            {/* Status Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex items-center gap-3">
                <div className="relative w-64">
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !filters.status.includes(e.target.value)) {
                        handleStatusChange(e.target.value)
                      }
                      e.target.value = ""
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select status to add...</option>
                    {['PENDING', 'IN_PROGRESS', 'APPROVED', 'DECLINED'].map(status => (
                      <option 
                        key={status} 
                        value={status} 
                        disabled={filters.status.includes(status)}
                        className={filters.status.includes(status) ? 'text-gray-400' : ''}
                      >
                        {filters.status.includes(status) ? `${status} (Already selected)` : status}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Selected Status Chips */}
                {filters.status.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {filters.status.map(status => (
                      <div
                        key={status}
                        className={`group inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md ${
                          status === 'PENDING' ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200' :
                          status === 'IN_PROGRESS' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200' :
                          status === 'APPROVED' ? 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200' :
                          status === 'DECLINED' ? 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200' :
                          status === 'SENT' ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200' :
                          status === 'FAILED' ? 'bg-gradient-to-r from-red-50 to-pink-50 text-red-700 border border-red-200' :
                          'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border border-gray-200'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${
                          status === 'PENDING' ? 'bg-amber-400' :
                          status === 'IN_PROGRESS' ? 'bg-blue-400' :
                          status === 'APPROVED' ? 'bg-emerald-400' :
                          status === 'DECLINED' ? 'bg-red-400' :
                          status === 'SENT' ? 'bg-green-400' :
                          status === 'FAILED' ? 'bg-red-400' :
                          'bg-gray-400'
                        }`}></div>
                        <span className="font-medium">{status}</span>
                        <button
                          onClick={() => handleStatusChange(status)}
                          className="opacity-60 hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-white hover:bg-opacity-50 rounded-md"
                          title="Remove filter"
                        >
                          <X size={14} className="text-black" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Clear Filters */}
            {(filters.search || filters.status.length > 1) && (
              <button
                onClick={() => {
                  setFilters({
                    search: '',
                    status: ['PENDING']
                  })
                }}
                className="bg-gray-500 text-white px-3 py-2 rounded text-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center gap-2"
              >
                <XCircle size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      {/* Results Section */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">
            Notification Queue
          </h3>
          <div className="flex items-center gap-4 mt-1">
            {filteredAttachments.length > 0 && (
              <p className="text-sm text-gray-600">
                Found {filteredAttachments.length} notification{filteredAttachments.length !== 1 ? 's' : ''}
              </p>
            )}
            {filters.search && (
              <span className="text-sm text-blue-600">
                Searching: "{filters.search}"
              </span>
            )}
            {filters.status.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <div className="flex gap-1">
                  {filters.status.map(status => (
                    <div key={status} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                      status === 'PENDING' ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200' :
                      status === 'IN_PROGRESS' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200' :
                      status === 'APPROVED' ? 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200' :
                      status === 'DECLINED' ? 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200' :
                      status === 'SENT' ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200' :
                      status === 'FAILED' ? 'bg-gradient-to-r from-red-50 to-pink-50 text-red-700 border border-red-200' :
                      'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border border-gray-200'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        status === 'PENDING' ? 'bg-amber-400' :
                        status === 'IN_PROGRESS' ? 'bg-blue-400' :
                        status === 'APPROVED' ? 'bg-emerald-400' :
                        status === 'DECLINED' ? 'bg-red-400' :
                        status === 'SENT' ? 'bg-green-400' :
                        status === 'FAILED' ? 'bg-red-400' :
                        'bg-gray-400'
                      }`}></div>
                      <span>{status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="inline-block animate-spin h-8 w-8 text-blue-600" />
            <p className="mt-2 text-gray-600">Loading attachments...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-600 mb-2">⚠️ Error</div>
            <p className="text-gray-600">{error}</p>
            <button
              onClick={fetchAttachments}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : filteredAttachments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-500 mb-2">📄 No notifications found</div>
            <p className="text-gray-600">No notification queue items match your current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient's Whatsapp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAttachments.map((notification, index) => (
                  <tr key={notification.notificationQueueId || index} className="hover:bg-gray-50">
                   
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {notification.notificationQueue?.recipientWhatsapp || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {notification.notificationQueue?.recipientEmail || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {notification.notificationQueue?.subject || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                        getDisplayStatus(notification) === 'PENDING'
                          ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200'
                          : getDisplayStatus(notification) === 'IN_PROGRESS'
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200'
                            : getDisplayStatus(notification) === 'APPROVED'
                              ? 'bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200'
                              : getDisplayStatus(notification) === 'DECLINED'
                                ? 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200'
                                : getDisplayStatus(notification) === 'SENT'
                                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200'
                                  : getDisplayStatus(notification) === 'FAILED'
                                    ? 'bg-gradient-to-r from-red-50 to-pink-50 text-red-700 border border-red-200'
                                    : 'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border border-gray-200'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          getDisplayStatus(notification) === 'PENDING' ? 'bg-amber-400' :
                          getDisplayStatus(notification) === 'IN_PROGRESS' ? 'bg-blue-400' :
                          getDisplayStatus(notification) === 'APPROVED' ? 'bg-emerald-400' :
                          getDisplayStatus(notification) === 'DECLINED' ? 'bg-red-400' :
                          
                          'bg-gray-400'
                        }`}></div>
                        <span>{getDisplayStatus(notification)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {notification.notificationQueue?.scheduledAt 
                        ? formatDate(notification.notificationQueue.scheduledAt)
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <ViewButton
                          onClick={() => handleViewNotification(notification)}
                          title="View Details"
                        />
                        {getDisplayStatus(notification) === 'PENDING' && (
                          <>
                            <button
                              onClick={() => handleApproveNotification(notification)}
                              className="bg-green-600 text-white px-4 py-2 rounded text-xs hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleDeclineNotification(notification)}
                              className="bg-red-600 text-white px-4 py-2 rounded text-xs hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                              Decline
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dark Overlay with Blur */}
      {viewModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" onClick={closeViewModal} />
      )}

      {/* Document View Modal */}
      <Dialog
        open={viewModalOpen}
        handler={closeViewModal}
        size="xl"
        className="z-50"
      >
        <DialogContent className="p-0 h-[95vh] w-[8vw] overflow-hidden">
          {selectedNotification && (
            <div className="flex flex-col h-full">
              {/* Minimal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b bg-white shadow-sm">
                <div className="flex items-center gap-6">
                  <h3 className="text-xl font-semibold text-gray-900">Document Viewer</h3>
                  {getDisplayStatus(selectedNotification) === 'PENDING' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewMode('view')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          viewMode === 'view' 
                            ? 'bg-blue-600 text-white shadow-sm' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Eye size={16} className="inline mr-2" />
                        View
                      </button>
                      <button
                        onClick={() => setViewMode('edit')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          viewMode === 'edit' 
                            ? 'bg-emerald-600 text-white shadow-sm' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Edit3 size={16} className="inline mr-2" />
                        Edit
                      </button>
                    </div>
                  )}
                  {viewMode === 'edit' && hasUnsavedChanges && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm font-medium text-amber-700">Unsaved Changes</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {viewMode === 'edit' && getDisplayStatus(selectedNotification) === 'PENDING' && (
                    <button
                      onClick={() => {
                        if (selectedNotification.notificationAttachment?.[0]) {
                          handleSaveAttachment();
                        }
                      }}
                      disabled={!hasUnsavedChanges}
                      className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          Save
                        </>
                      )}
                    </button>
                  )}
                  <CloseButton
                    onClick={closeViewModal}
                    className=" text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                   
                  </CloseButton>
                </div>
              </div>

              {/* Document Container */}
              <div className="flex-1 bg-gray-50 overflow-hidden">
                {selectedNotification.notificationAttachment && selectedNotification.notificationAttachment.length > 0 ? (
                  (() => {
                    const attachment = selectedNotification.notificationAttachment[0];
                    const isPdf = isPdfContent(attachment.content);
                    const displayStatus = getDisplayStatus(selectedNotification);
                    
                    // For PDF content (APPROVED, IN_PROGRESS, DECLINED) or when not in edit mode
                    if (isPdf || (displayStatus !== 'PENDING' && viewMode === 'view')) {
                      const pdfUrl = createPdfBlobUrl(attachment.content);
                      
                      return (
                        <div className="h-full w-full">
                          {pdfUrl ? (
                            <iframe
                              src={pdfUrl}
                              className="w-full h-full border-0"
                              title="PDF Preview"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center text-gray-500">
                                <div className="text-4xl mb-4">📄</div>
                                <h3 className="text-lg font-medium mb-2">PDF Preview Error</h3>
                                <p className="text-sm">Unable to display PDF content.</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // For HTML content (PENDING status) - show edit/preview interface
                    return (
                      <div className="h-full w-full flex items-center justify-center p-8">
                        {/* Document Container */}
                        <div className="bg-white shadow-2xl rounded-lg overflow-hidden w-full max-w-4xl h-full max-h-[calc(100vh-200px)]">
                          {/* Document Content */}
                          <div className="h-full overflow-y-auto p-8 relative">
                            {viewMode === 'edit' ? (
                              <div
                                ref={setEditorRef}
                                dangerouslySetInnerHTML={{ 
                                  __html: editingContent || convertContentToHtml(attachment.content)
                                }}
                                contentEditable={isEditing}
                                onClick={() => setIsEditing(true)}
                                className="h-full focus:outline-none prose prose-lg max-w-none"
                                style={{
                                  fontFamily: 'system-ui, -apple-system, sans-serif',
                                  lineHeight: '1.7',
                                  fontSize: '16px',
                                  color: '#1f2937',
                                  cursor: isEditing ? 'text' : 'pointer'
                                }}
                              />
                            ) : (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: convertContentToHtml(attachment.content)
                                }}
                                className="h-full prose prose-lg max-w-none"
                                style={{
                                  fontFamily: 'system-ui, -apple-system, sans-serif',
                                  lineHeight: '1.7',
                                  fontSize: '16px',
                                  color: '#1f2937'
                                }}
                              />
                            )}
                            
                            {/* Edit Mode Indicator */}
                            {viewMode === 'edit' && (
                              <div className="absolute top-4 right-4">
                                <div className="bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm">
                                  {isEditing ? '✏️ Editing' : '👆 Click to Edit'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-500">
                      <div className="text-4xl mb-4">📄</div>
                      <h3 className="text-lg font-medium mb-2">No Attachments</h3>
                      <p className="text-sm">This notification has no attachments to display.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dark Overlay with Blur for Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" onClick={handleCancelEdit} />
      )}

      {/* Edit Content Modal */}
      <Dialog
        open={editModalOpen}
        handler={handleCancelEdit}
        size="xl"
        className="z-50"
      >
        <DialogContent className="p-6 max-h-[105vh] max-w-[95vw] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Edit Content</h3>
            <CloseButton onClick={handleCancelEdit} />
          </div>

          {editingAttachment && (
            <div className="space-y-4">
              {/* Split View Editor */}
              <div className="border rounded-lg bg-white min-h-[650px]">
                {/* Edit Controls */}
                <div className="border-b p-3  flex justify-between items-center">
                  <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Edit Mode - Live Preview</span>
                    {hasUnsavedChanges && (
                      <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        ⚠️ Unsaved Changes
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <OutlineButton
                      onClick={handleSaveAttachment}
                      disabled={isSaving || !hasUnsavedChanges}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          
                          Save Changes
                        </>
                      )}
                    </OutlineButton>
                   
                  </div>
                </div>
                
                {/* Split View */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 min-h-[600px]">
                  {/* ContentEditable Editor Side */}
                  <div className="border-r">
                  <div className="p-3  border-b">
                      <span className="text-sm font-medium text-gray-600">Content Editor</span>
                    </div>
                    
                    {/* ContentEditable Editor */}
                    <div className="h-[600px] overflow-y-auto p-4">
                      <div
                        ref={setEditorRef}
                        dangerouslySetInnerHTML={{ __html: editingContent }}
                        onClick={(e) => {
                          console.log('Editor clicked:', e.target);
                          console.log('Editor contenteditable:', e.target.getAttribute('contenteditable'));
                        }}
                        className="prose max-w-none focus:outline-none"
                        style={{
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          lineHeight: '1.4',
                          
                          padding: '20px',
                          minHeight: '450px'
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Preview Side */}
                  <div>
                    <div className="p-3  border-b">
                      <span className="text-sm font-medium text-gray-600">Live Preview</span>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{ __html: previewContent || editingContent }}
                      className="h-[600px] overflow-y-auto prose max-w-none"
                      style={{
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                     
                        padding: '20px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dark Overlay with Blur for Approval Modal */}
      {approvalModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" onClick={() => setApprovalModalOpen(false)} />
      )}

      {/* Approval Modal */}
      <Dialog
        open={approvalModalOpen}
        handler={() => setApprovalModalOpen(false)}
        size="sm"
        className="z-50"
      >
        <DialogContent className="p-0 overflow-hidden">
          {/* Minimal Header */}
          <div className="flex justify-between items-center px-4 py-3 border-b bg-white">
            <h3 className="text-base font-semibold text-gray-900">
              {approvalStatus === 'APPROVED' ? 'Schedule Notification' : 'Decline Notification'}
            </h3>
            <CloseButton onClick={() => setApprovalModalOpen(false)} />
          </div>

          <div className="p-4 space-y-4">
            {/* Action Description */}
            <div className="text-center py-2">
              <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full mb-2 ${
                approvalStatus === 'APPROVED' ? 'bg-blue-50' : 'bg-gray-50'
              }`}>
                {approvalStatus === 'APPROVED' ? (
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">
                {approvalStatus === 'APPROVED' ? 'Approve & Schedule' : 'Decline Notification'}
              </h4>
              <p className="text-xs text-gray-600">
                {approvalStatus === 'APPROVED' 
                  ? 'Schedule this notification for sending.'
                  : 'Mark this notification as declined.'
                }
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-3">
              {approvalStatus === 'APPROVED' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Scheduled Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Passphrase <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter passphrase"
                  required
                />
              </div>
            </div>

            

            {/* Action Buttons */}
            <div className="flex gap-2 pt-1">
              
              <PrimaryButton
                onClick={handleApproveAndSend}
                disabled={isApproving || !passphrase || (approvalStatus === 'APPROVED' && !scheduledDate)}
                // className={`flex-1 px-3 py-1.5 text-xs font-medium text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                //   approvalStatus === 'APPROVED' 
                //     ? 'bg-blue-600 hover:bg-blue-700' 
                //     : 'bg-gray-600 hover:bg-gray-700'
                // }`}
                className={"w-full"}
              >
                {isApproving ? (
                  <div className="flex items-center justify-center gap-1">
                    <Loader2 size={12} className="animate-spin" />
                    {approvalStatus === 'APPROVED' ? 'Scheduling...' : 'Declining...'}
                  </div>
                ) : (
                  approvalStatus === 'APPROVED' ? 'Approve & Schedule' : 'Decline'
                )}
              </PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DemandLetterApproval